import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// Standalone site-availability board. Intentionally unrelated to the client
// reports — nothing here is sent to a client or embedded in a PDF.

const PERIODS = [
  { days: 7, label: '7 ימים' },
  { days: 30, label: '30 יום' },
  { days: 90, label: '90 יום' },
];

// The probe runs every 5 minutes; anything much older means probing itself
// stopped (worker restart, deploy, reboot) and the numbers are going stale.
const STALE_AFTER_MS = 15 * 60 * 1000;

// FastAPI serialises naive datetime.utcnow() values with no timezone suffix.
// JS would parse those as local time — 3 hours off in Israel — so treat a
// missing offset as UTC.
function parseUtc(value) {
  if (!value) return null;
  const s = String(value);
  const hasZone = /([Zz]|[+-]\d{2}:?\d{2})$/.test(s);
  const d = new Date(hasZone ? s : s + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

// Hebrew "and": the hyphen belongs before a numeral only — "שעה ו-12 דקות"
// but "שעה ודקה". Mirrors _join_he on the backend.
function joinHe(head, tail) {
  return /^\d/.test(tail) ? head + ' ו-' + tail : head + ' ו' + tail;
}

// Mirrors _fmt_duration in backend/app/tasks/monitoring.py so the screen and
// the alert emails describe the same outage the same way.
function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return 'פחות מדקה';
  let m = Math.floor(s / 60);
  if (m < 60) return m === 1 ? 'דקה' : m + ' דקות';
  let h = Math.floor(m / 60);
  m = m % 60;
  if (h < 24) {
    const head = h === 1 ? 'שעה' : h + ' שעות';
    if (!m) return head;
    return joinHe(head, m === 1 ? 'דקה' : m + ' דקות');
  }
  const d = Math.floor(h / 24);
  h = h % 24;
  const head = d === 1 ? 'יום' : d + ' ימים';
  if (!h) return head;
  return joinHe(head, h === 1 ? 'שעה' : h + ' שעות');
}

function fmtAgo(date, now) {
  if (!date) return 'לא נבדק מעולם';
  const secs = Math.round((now - date) / 1000);
  if (secs < 60) return 'לפני רגע';
  return 'לפני ' + fmtDuration(secs);
}

function fmtWhen(date) {
  return date ? date.toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

function uptimeColor(pct) {
  if (pct >= 99.9) return 'var(--green)';
  if (pct >= 99) return 'var(--orange)';
  return 'var(--red)';
}

function StatusPill({ status }) {
  const map = {
    up: { text: 'תקין', color: 'var(--green)' },
    down: { text: 'למטה', color: 'var(--red)' },
    unknown: { text: 'לא ידוע', color: 'var(--text-muted)' },
  };
  const s = map[status] || map.unknown;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      fontSize: '12px', fontWeight: 600, color: s.color,
    }}>
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.color }} />
      {s.text}
    </span>
  );
}

function Tile({ label, value, color }) {
  return (
    <div style={{
      flex: '1 1 160px', background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: '10px', padding: '14px 16px',
    }}>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 800, color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}

export default function Uptime() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [history, setHistory] = useState({});
  const [now, setNow] = useState(() => new Date());

  const load = useCallback((quiet) => {
    if (!quiet) setError(null);
    api.getUptimeOverview(days)
      .then((d) => { setData(d); setNow(new Date()); setError(null); })
      .catch((err) => { if (!quiet) setError(err.message); });
  }, [days]);

  useEffect(() => { load(false); }, [load]);

  // The board is a live status view — refresh between probe runs so a site
  // that just went down doesn't sit unnoticed behind a stale page.
  useEffect(() => {
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  function toggle(siteId) {
    if (expanded === siteId) { setExpanded(null); return; }
    setExpanded(siteId);
    if (history[siteId]) return;
    setHistory((h) => ({ ...h, [siteId]: { loading: true } }));
    api.getSiteIncidents(siteId, 90)
      .then((d) => setHistory((h) => ({ ...h, [siteId]: { loading: false, data: d } })))
      .catch((err) => setHistory((h) => ({ ...h, [siteId]: { loading: false, error: err.message } })));
  }

  if (error && !data) return <div className="loading">שגיאה בטעינת נתונים: {error}</div>;
  if (!data) return <div className="loading">טוען...</div>;

  const q = search.trim().toLowerCase();
  const rows = data.sites
    .filter((r) => !q || (r.name || '').toLowerCase().includes(q) || (r.url || '').toLowerCase().includes(q))
    // Worst first: sites that are down now, then by how long they were down.
    .slice()
    .sort((a, b) => {
      const ad = a.monitor_status === 'down' ? 1 : 0;
      const bd = b.monitor_status === 'down' ? 1 : 0;
      if (ad !== bd) return bd - ad;
      if (a.downtime_seconds !== b.downtime_seconds) return b.downtime_seconds - a.downtime_seconds;
      return (a.name || '').localeCompare(b.name || '', 'he');
    });

  const t = data.totals;
  const staleCount = data.sites.filter((r) => {
    const c = parseUtc(r.monitor_checked_at);
    return !c || (now - c) > STALE_AFTER_MS;
  }).length;

  return (
    <>
      <div style={{ margin: '4px 2px 20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text)' }}>זמינות אתרים</h1>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '3px' }}>
          ניטור נפילות לכל האתרים הפעילים — בדיקה כל 5 דקות. לחיצה על שורה מציגה את היסטוריית הנפילות.
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <Tile label="אתרים במעקב" value={t.sites} />
        <Tile label="למטה כרגע" value={t.down_now} color={t.down_now ? 'var(--red)' : 'var(--green)'} />
        <Tile label={'נפילות ב-' + days + ' הימים האחרונים'} value={t.incidents} />
        <Tile label="סה״כ זמן השבתה" value={t.downtime_seconds ? fmtDuration(t.downtime_seconds) : '—'} />
      </div>

      {staleCount > 0 && (
        <div style={{
          padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
          background: 'rgba(245,158,11,0.1)', color: 'var(--orange)', border: '1px solid rgba(245,158,11,0.25)',
        }}>
          {staleCount === data.sites.length
            ? 'אף אתר לא נבדק ב-15 הדקות האחרונות — כנראה שתהליך הניטור (maxrank-celery-beat) אינו רץ.'
            : staleCount + ' אתרים לא נבדקו ב-15 הדקות האחרונות. ייתכן שהניטור נעצר עבורם.'}
        </div>
      )}

      <div className="filter-row">
        <div className="topbar-search" style={{ width: '300px' }}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="7" cy="7" r="5" /><path d="m11 11 3 3" />
          </svg>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="חיפוש אתר..." />
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {PERIODS.map((p) => (
            <div key={p.days} className={'filter-chip ' + (days === p.days ? 'active' : '')}
              onClick={() => { setDays(p.days); setExpanded(null); }}>
              {p.label}
            </div>
          ))}
        </div>
      </div>

      <div className="sites-table-wrapper animate-in">
        <table className="sites-table">
          <thead>
            <tr>
              <th>אתר</th>
              <th>סטטוס</th>
              <th>זמינות</th>
              <th>נפילות</th>
              <th>סה״כ השבתה</th>
              <th>הארוכה ביותר</th>
              <th>נבדק לאחרונה</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px' }}>
                לא נמצאו אתרים
              </td></tr>
            )}
            {rows.map((r) => {
              const checked = parseUtc(r.monitor_checked_at);
              const stale = !checked || (now - checked) > STALE_AFTER_MS;
              const downSince = parseUtc(r.down_since);
              const hist = history[r.site_id];
              return (
                <React.Fragment key={r.site_id}>
                  <tr onClick={() => toggle(r.site_id)}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        <span style={{ color: 'var(--text-muted)', marginLeft: '6px', fontSize: '11px' }}>
                          {expanded === r.site_id ? '▾' : '▸'}
                        </span>
                        {r.name}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', direction: 'ltr', textAlign: 'right' }}>
                        {(r.url || '').replace(/^https?:\/\//, '')}
                      </div>
                    </td>
                    <td>
                      <StatusPill status={r.monitor_status} />
                      {downSince && (
                        <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '2px' }}>
                          {fmtDuration((now - downSince) / 1000)}
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 700, color: uptimeColor(r.uptime_pct), direction: 'ltr', textAlign: 'right' }}>
                      {r.uptime_pct.toFixed(2)}%
                    </td>
                    <td>{r.incident_count || '—'}</td>
                    <td>{r.downtime_seconds ? fmtDuration(r.downtime_seconds) : '—'}</td>
                    <td>{r.longest_incident_seconds ? fmtDuration(r.longest_incident_seconds) : '—'}</td>
                    <td style={{ color: stale ? 'var(--orange)' : 'var(--text-secondary)', fontSize: '12px' }}>
                      {fmtAgo(checked, now)}
                    </td>
                  </tr>
                  {expanded === r.site_id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--bg)', padding: '14px 20px', whiteSpace: 'normal' }}>
                        {!hist || hist.loading ? (
                          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>טוען היסטוריה...</div>
                        ) : hist.error ? (
                          <div style={{ fontSize: '13px', color: 'var(--red)' }}>שגיאה: {hist.error}</div>
                        ) : hist.data.incidents.length === 0 ? (
                          <div style={{ fontSize: '13px', color: 'var(--green)' }}>
                            לא נרשמו נפילות ב-90 הימים האחרונים.
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                              {hist.data.incident_count} נפילות ב-90 הימים האחרונים · סה״כ {fmtDuration(hist.data.downtime_seconds)}
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ color: 'var(--text-muted)', textAlign: 'right' }}>
                                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>התחלה</th>
                                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>סיום</th>
                                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>משך</th>
                                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>סיבה</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hist.data.incidents.map((i) => (
                                  <tr key={i.id} style={{ borderTop: '1px solid var(--border)' }}>
                                    <td style={{ padding: '6px 8px' }}>{fmtWhen(parseUtc(i.started_at))}</td>
                                    <td style={{ padding: '6px 8px' }}>
                                      {i.ongoing
                                        ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>עדיין למטה</span>
                                        : fmtWhen(parseUtc(i.ended_at))}
                                    </td>
                                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{fmtDuration(i.duration_seconds)}</td>
                                    <td style={{ padding: '6px 8px', color: 'var(--text-muted)', direction: 'ltr', textAlign: 'right' }}>
                                      {i.status_code ? 'HTTP ' + i.status_code : (i.error || '—')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '12px', lineHeight: 1.6 }}>
        זמן ההשבתה נמדד מבדיקות שרצות כל 5 דקות, ונדרשות שתי בדיקות כושלות רצופות כדי לפתוח אירוע.
        אם הניטור עצמו נעצר, הפער אינו נספר כהשבתה — ולכן יש להתייחס לאחוזים כרצפה ולא כמדד SLA מחייב.
      </div>
    </>
  );
}
